import React from 'react';
import symbolLegendManager from '@client/utils/SymbolLegendManager';

/**
 * SVG component for low risk level (circular shape)
 * @param {Object} props - Component props
 * @param {string} props.img - Image URL to embed in SVG
 * @param {number} props.width - SVG width
 * @param {number} props.height - SVG height
 * @param {number} props.imageWidth - Image width
 * @param {number} props.imageHeight - Image height
 * @returns {JSX.Element} SVG element
 */
const LowRiskSvg = ({
  img,
  width = 33,
  height = 32,
  imageWidth = 17,
  imageHeight = 16,
}) => (
  <svg
    width={width}
    height={height}
    viewBox="0 0 33 32"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M16.5 30C24.5666 30 31 23.6744 31 16C31 8.32558 24.5666 2 16.5 2C8.43341 2 2 8.32558 2 16C2 23.6744 8.43341 30 16.5 30ZM16.5 32C25.6127 32 33 24.8366 33 16C33 7.16344 25.6127 0 16.5 0C7.3873 0 0 7.16344 0 16C0 24.8366 7.3873 32 16.5 32Z"
      fill="#389E0D"
    />
    {img && (
      <image
        href={img}
        x="8"
        y="8"
        width={imageWidth}
        height={imageHeight}
        preserveAspectRatio="xMidYMid meet"
      />
    )}
  </svg>
);

/**
 * SVG component for slight risk level (half-circle shape)
 * @param {Object} props - Component props
 * @param {string} props.img - Image URL to embed in SVG
 * @param {number} props.width - SVG width
 * @param {number} props.height - SVG height
 * @param {number} props.imageWidth - Image width
 * @param {number} props.imageHeight - Image height
 * @returns {JSX.Element} SVG element
 */
const SlightRiskSvg = ({
  img,
  width = 33,
  height = 32,
  imageWidth = 17,
  imageHeight = 20,
}) => (
  <svg
    width={width}
    height={height}
    viewBox="0 0 33 32"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M30.5 2H16.5C8.76801 2 2.5 8.26801 2.5 16C2.5 23.732 8.76801 30 16.5 30C24.232 30 30.5 23.732 30.5 16V2ZM16.5 0C7.66344 0 0.5 7.16344 0.5 16C0.5 24.8366 7.66344 32 16.5 32C25.3366 32 32.5 24.8366 32.5 16V0H16.5Z"
      fill="#389E0D"
    />
    {img && (
      <image
        href={img}
        x="8"
        y="6"
        width={imageWidth}
        height={imageHeight}
        preserveAspectRatio="xMidYMid meet"
      />
    )}
  </svg>
);

/**
 * SVG component for moderate risk level (triangle shape)
 * @param {Object} props - Component props
 * @param {string} props.img - Image URL to embed in SVG
 * @param {number} props.width - SVG width
 * @param {number} props.height - SVG height
 * @param {number} props.imageWidth - Image width
 * @param {number} props.imageHeight - Image height
 * @returns {JSX.Element} SVG element
 */
const ModerateRiskSvg = ({
  img,
  width = 41,
  height = 32,
  imageWidth = 20,
  imageHeight = 16,
}) => (
  <svg
    width={width}
    height={height}
    viewBox="0 0 41 32"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M20.5 32L0.5 0H40.5L20.5 32ZM20.5 28.2425L4.11009 2.01863H36.8899L20.5 28.2425Z"
      fill="#FFC069"
    />
    {img && (
      <image
        href={img}
        x="10"
        y="3"
        width={imageWidth}
        height={imageHeight}
        preserveAspectRatio="xMidYMid meet"
      />
    )}
  </svg>
);

/**
 * SVG component for high risk level (diamond shape)
 * @param {Object} props - Component props
 * @param {string} props.img - Image URL to embed in SVG
 * @param {number} props.width - SVG width
 * @param {number} props.height - SVG height
 * @param {number} props.imageWidth - Image width
 * @param {number} props.imageHeight - Image height
 * @returns {JSX.Element} SVG element
 */
const HighRiskSvg = ({
  img,
  width = 33,
  height = 32,
  imageWidth = 17,
  imageHeight = 16,
}) => (
  <svg
    width={width}
    height={height}
    viewBox="0 0 33 32"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M16.5 2.7859L2.87296 16L16.5 29.2141L30.127 16L16.5 2.7859ZM16.5 0L0 16L16.5 32L33 16L16.5 0Z"
      fill="#CF1322"
    />
    {img && (
      <image
        href={img}
        x="8"
        y="8"
        width={imageWidth}
        height={imageHeight}
        preserveAspectRatio="xMidYMid meet"
      />
    )}
  </svg>
);

/**
 * SVG component for extremely high risk level (octagon shape)
 * @param {Object} props - Component props
 * @param {string} props.img - Image URL to embed in SVG
 * @param {number} props.width - SVG width
 * @param {number} props.height - SVG height
 * @param {number} props.imageWidth - Image width
 * @param {number} props.imageHeight - Image height
 * @returns {JSX.Element} SVG element
 */
const ExtremeRiskSvg = ({
  img,
  width = 32,
  height = 32,
  imageWidth = 20,
  imageHeight = 20,
}) => (
  <svg
    width={width}
    height={height}
    viewBox="0 0 32 32"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M16 2.16478L6.21703 6.21703L2.16478 16L6.21703 25.783L16 29.8352L25.783 25.783L29.8352 16L25.783 6.21703L16 2.16478ZM27.3137 4.68629L16 0L4.68629 4.68629L0 16L4.68629 27.3137L16 32L27.3137 27.3137L32 16L27.3137 4.68629Z"
      fill="#FF4D4F"
    />
    {img && (
      <image
        href={img}
        x="6"
        y="6"
        width={imageWidth}
        height={imageHeight}
        preserveAspectRatio="xMidYMid meet"
      />
    )}
  </svg>
);

/**
 * Risk level to SVG component mapping
 */
const RISK_SVG_COMPONENTS = {
  1: LowRiskSvg,
  2: SlightRiskSvg,
  3: ModerateRiskSvg,
  4: HighRiskSvg,
  5: ExtremeRiskSvg,
};

/**
 * Size mappings for component display
 */
const SIZE_MAP = {
  sm: { width: 32, height: 32, imageWidth: 16, imageHeight: 16 },
  md: { width: 48, height: 48, imageWidth: 24, imageHeight: 24 },
  lg: { width: 64, height: 64, imageWidth: 32, imageHeight: 32 },
};

/**
 * Fallback display component for invalid data
 * @returns {JSX.Element} Fallback display
 */
const FallbackDisplay = () => <div>N/A</div>;

/**
 * Risk Symbol Display Component
 *
 * Renders risk symbols with appropriate SVG shapes and colors based on risk levels.
 * Integrates with SymbolLegendManager for consistent label-based risk level mapping.
 * Supports multiple sizes and automatically handles various data formats.
 *
 * @component
 * @example
 * // Basic usage with symbol data
 * <RiskSymbolDisplay
 *   symbolData={{
 *     calculated_value: 3,
 *     symbol: { label: "moderate", image: "/path/to/image.png" }
 *   }}
 *   size="md"
 * />
 *
 * @example
 * // Usage with numeric value
 * <RiskSymbolDisplay symbolData={4} size="lg" />
 *
 * @param {Object} props - Component props
 * @param {Object|number|null} props.symbolData - Symbol data object or numeric risk level
 * @param {number} [props.symbolData.calculated_value] - Calculated risk value
 * @param {Object} [props.symbolData.symbol] - Symbol metadata object
 * @param {string} [props.symbolData.symbol.label] - Display label for risk level
 * @param {string} [props.symbolData.symbol.image] - Image URL for symbol
 * @param {'sm'|'md'|'lg'} [props.size='md'] - Display size variant
 * @param {number} [props.width] - Custom width (overrides size)
 * @param {number} [props.height] - Custom height (overrides size)
 * @param {number} [props.imageWidth] - Custom image width (overrides size)
 * @param {number} [props.imageHeight] - Custom image height (overrides size)
 * @returns {JSX.Element} Rendered risk symbol component
 */
const RiskSymbolDisplay = ({
  symbolData,
  size = 'sm',
  width,
  height,
  imageWidth,
  imageHeight,
}) => {
  /**
   * Processes symbol data and extracts display information
   * @returns {Object|null} Processed symbol information or null if invalid
   */
  const getSymbolInfo = () => {
    if (!symbolData) return null;

    if (typeof symbolData === 'number') {
      return {
        value: symbolData,
        image: null,
        label: symbolData.toString(),
      };
    }

    if (symbolData?.symbol || symbolData?.calculated_value) {
      const label =
        symbolData.symbol?.label || symbolData.calculated_value?.toString();

      if (!label) return null;

      const displayInfo = symbolLegendManager.getSymbolDisplay(label);
      const image = symbolData.symbol?.image || null;

      return {
        value: displayInfo.order || 1,
        image,
        label: displayInfo.label,
        displayInfo,
      };
    }

    return null;
  };

  const symbolInfo = getSymbolInfo();

  if (!symbolInfo) {
    return <FallbackDisplay />;
  }

  const sizeConfig = SIZE_MAP[size] || SIZE_MAP.md;
  const finalWidth = width || sizeConfig.width;
  const finalHeight = height || sizeConfig.height;
  const finalImageWidth = imageWidth || sizeConfig.imageWidth;
  const finalImageHeight = imageHeight || sizeConfig.imageHeight;

  const SvgComponent =
    RISK_SVG_COMPONENTS[symbolInfo.value] || RISK_SVG_COMPONENTS[1];

  return (
    <div
      className="flex justify-center items-center"
      style={{ width: finalWidth, height: finalHeight }}
      title={symbolInfo.label}
    >
      <SvgComponent
        img={symbolInfo.image}
        width={finalWidth}
        height={finalHeight}
        imageWidth={finalImageWidth}
        imageHeight={finalImageHeight}
      />
    </div>
  );
};

export default RiskSymbolDisplay;
